/**
 * Locale Provider — active UI language (en / fr / it).
 *
 * Mirrors ThemeProvider: localStorage for the anonymous choice, plus a
 * round-trip to the user's stored preference once authenticated (so the
 * language follows the account across devices). i18next is the engine; this
 * provider owns persistence and keeps <html lang> in sync.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LOCALES,
  resolveLocale,
  type Locale,
} from '@nord-living/shared';

import { useAuth } from '@/providers/AuthProvider';
import {
  useUserPreferences,
  useUpdateUserPreferences,
} from '@/hooks/useUserPreferences';
import { LOCALE_STORAGE_KEY } from '@/i18n';

interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  supportedLocales: readonly Locale[];
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { data: prefs } = useUserPreferences(isAuthenticated);
  const updatePrefs = useUpdateUserPreferences();
  const [locale, setLocaleState] = useState<Locale>(() =>
    resolveLocale(i18n.language),
  );

  // Keep i18next + <html lang> aligned with the active locale.
  useEffect(() => {
    void i18n.changeLanguage(locale);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [i18n, locale]);

  // Adopt the authenticated user's stored locale when it loads.
  useEffect(() => {
    if (!prefs?.locale) return;
    const resolved = resolveLocale(prefs.locale);
    window.localStorage.setItem(LOCALE_STORAGE_KEY, resolved);
    setLocaleState((current) => (current === resolved ? current : resolved));
  }, [prefs?.locale]);

  const setLocale = useCallback(
    (next: Locale) => {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
      setLocaleState(next);
      if (isAuthenticated) updatePrefs.mutate({ locale: next });
    },
    [isAuthenticated, updatePrefs],
  );

  return (
    <LocaleContext.Provider
      value={{ locale, setLocale, supportedLocales: SUPPORTED_LOCALES }}
    >
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return ctx;
}
